'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Environment {
    label: string;
    verb: string;
    img: string;
    alt: string;
    color: string;
    textColor: string;
}

const environments: Environment[] = [
    {
        label: 'Kitchen',
        verb: 'Cook',
        img: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80',
        alt: 'Chef teaching cooking technique in a warm professional kitchen, dark moody atmosphere, dim overhead lighting, rich shadows',
        color: 'from-orange/60 to-transparent',
        textColor: 'text-white'
    },
    {
        label: 'Workshop',
        verb: 'Build',
        img: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80',
        alt: 'Carpenter teaching woodworking in a workshop, sawdust in air, dim warm workshop lighting, deep shadows on wooden walls',
        color: 'from-accent/60 to-transparent',
        textColor: 'text-white'
    },
    {
        label: 'Salon',
        verb: 'Style',
        img: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&q=80',
        alt: 'Hairstylist teaching hair styling in a modern salon, warm atmospheric lighting, dark mirror reflections, professional setting',
        color: 'from-coral/60 to-transparent',
        textColor: 'text-white'
    },
    {
        label: 'Studio',
        verb: 'Create',
        img: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&q=80',
        alt: 'Musician teaching guitar in a recording studio, dim studio lighting, dark acoustic panels, atmospheric glow from monitors',
        color: 'from-purple/60 to-transparent',
        textColor: 'text-white'
    },
    {
        label: 'Computer',
        verb: 'Code',
        img: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80',
        alt: 'Developer teaching programming, bright screen glow in dim room, code visible on dark screen, atmospheric blue lighting',
        color: 'from-primary/60 to-transparent',
        textColor: 'text-white'
    },
    {
        label: 'Classroom',
        verb: 'Teach',
        img: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=600&q=80',
        alt: 'Teacher explaining mathematics on a whiteboard, dim classroom with focused overhead lighting, dark window backgrounds',
        color: 'from-green/60 to-transparent',
        textColor: 'text-white'
    }];


export default function LearningIsEverywhereSection() {
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                    }
                });
            },
            { threshold: 0.12 }
        );

        const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
        els?.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="relative py-20 md:py-28 bg-foreground overflow-hidden">
            {/* Background texture */}
            <div className="absolute inset-0 pointer-events-none opacity-5"
                style={{
                    backgroundImage: 'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
                    backgroundSize: '60px 60px'
                }} />

            <div className="absolute top-0 right-0 w-[500px] h-[500px] blob-primary opacity-20 translate-x-1/3 -translate-y-1/3 pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                {/* Header */}
                <div className="max-w-3xl mb-14 md:mb-20">
                    <div className="reveal mb-5">
                        <span className="pill-tag border border-white/20 text-white/70 bg-white/5 text-xs uppercase tracking-wider">
                            The big idea
                        </span>
                    </div>
                    <h2 className="reveal text-section-title font-extrabold text-white mb-5 delay-100">
                        Learning isn't a classroom.
                    </h2>
                    <p className="reveal text-lg text-white/60 font-body leading-relaxed max-w-2xl delay-200">
                        It can be a kitchen. A workshop. A salon. A studio. A computer. Learning happens
                        wherever someone is willing to share knowledge and someone else is ready to grow — in real time.
                    </p>
                </div>

                {/* Photo grid — 6 rotated cards */}
                <div className="reveal delay-300">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 items-end pb-4">
                        {environments.map((env, i) => {
                            const photoClasses = [
                                'photo-card-1',
                                'photo-card-2',
                                'photo-card-3',
                                'photo-card-4',
                                'photo-card-5',
                                'photo-card-6'];

                            return (
                                <div
                                    key={env.label}
                                    className={`relative group cursor-pointer transition-all duration-500 ease-out will-change-transform ${photoClasses[i]}`}
                                    style={{ transitionDelay: `${i * 60}ms` }}>

                                    <div className="aspect-[3/4] overflow-hidden rounded-2xl border-2 border-white/10 shadow-card-lg bg-foreground">
                                        <AppImage
                                            src={env.img}
                                            alt={env.alt}
                                            width={240}
                                            height={320}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />

                                        {/* Scrim */}
                                        <div className={`absolute inset-0 bg-gradient-to-t ${env.color} via-black/20 to-transparent`} />
                                        {/* Label */}
                                        <div className="absolute bottom-0 left-0 right-0 p-3">
                                            <p className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-0.5">{env.label}</p>
                                            <p className="text-white font-extrabold text-lg leading-none">{env.verb}</p>
                                        </div>
                                    </div>
                                </div>);

                        })}
                    </div>
                </div>

                {/* Bottom line */}
                <div className="reveal mt-14 delay-400 flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-10 border-t border-white/10">
                    <p className="text-white/50 text-base font-body">
                        Wisly connects them all.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {['Cook', 'Create', 'Build', 'Code', 'Design', 'Teach', 'Practice', 'Grow'].map((v) =>
                            <span key={v} className="pill-tag border border-white/15 text-white/60 bg-white/5 text-xs">
                                {v}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </section>);

}